import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types';
import {makeStyles, Typography} from "@material-ui/core";
import clsx from 'clsx';
import * as Yup from "yup";
import _ from "lodash";
import {connect} from "react-redux";
import moment from "libs/moment";
import GridBlock from "components/GridBlock";
import DateFormats from "constants/DateFormats";
import BoldTypography from "components/BoldTypography";
import Tr from "translations/Translations";
import NewMessageForm from "./NewMessageForm";
import VS from "constants/ValidationShema";
import Form from "components/Form";
import useId from "hooks/useId";
import CorrespondenceService from "services/CorrespondenceService";
import isResponseValid from "libs/isResponseValid";
import PublishMessageConfirmationPopup, {createConfirmObject} from "./PublishMessageConfirmationPopup";

const useStyles = makeStyles(theme => ({
    root: {
        width: '80%',
        margin: 5,
        marginLeft: '20%',
    },
    contentWrapper: {
        border: `solid 1px ${theme.palette.primary.main}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper
    },
    content: {
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`
    },
    sendDate: {
        margin: `0 ${theme.spacing(1)}px`,
        minWidth: 110
    },
    message: {
        whiteSpace: 'pre-line',
        margin: `${theme.spacing(2)}px 0`
    }
}));


function NewMessage(
    {
        rfxRoundId,
        listResourceId,
        contractor,
        message,
        threadId,
        formSubmit,
        currentActive
    }) {

    const classes = useStyles();
    const formId = useId();
    const [error, setError] = useState('');
    const [working, setWorking] = useState(false);
    const autoSaveSemaphore = useRef(false);
    const [publishMessageInfo, setPublishMessageInfo] = useState(null);
    const updateMessage = async (type, values) => {

        const messageOut = {
            attachments: values.attachments,
            contents: values.contents,
            recipients: contractor ? null : message.availableRecipients.filter(x => values.supplierRecipientIds.indexOf(x.id) > -1)
        };
        let holdSetWorking = false;
        try {
            let response;
            if (contractor) {
                response = await CorrespondenceService.updateContractorMessage(rfxRoundId, threadId, message.messageId, listResourceId, messageOut);
            } else {
                response = await CorrespondenceService.updateBuyerMessage(rfxRoundId, threadId, message.messageId, listResourceId, messageOut);
            }

            const isSaveRespValid = isResponseValid(response);
            if (isSaveRespValid && ("send" === type)) {
                let sendResp
                if (contractor) {
                    sendResp = await CorrespondenceService.sendContractorMessage(rfxRoundId, threadId, message.messageId, listResourceId);
                } else {
                    sendResp = await CorrespondenceService.sendBuyerMessage(rfxRoundId, threadId, message.messageId, listResourceId);
                }
                holdSetWorking |= isResponseValid(sendResp);
            } else if (isSaveRespValid && "publish" === type) {
                const publishResp = await CorrespondenceService.publishBuyerMessage(rfxRoundId, threadId, message.messageId, listResourceId);
                holdSetWorking |= isResponseValid(publishResp);
            }
        } catch (e) {
            console.error(e)
        }
        if (!holdSetWorking) {
            setWorking(false);
        }
    }

    const cancelPublish = (messageId) => {
        setPublishMessageInfo(null);
        setWorking(false);

    }
    const deleteMessage = async (messageId) => {
        setWorking(true);
        setError('');

        let delResp = null;
        if (contractor) {
            delResp = await CorrespondenceService.deleteContractorMessage(rfxRoundId, threadId, messageId, listResourceId);
        } else {
            delResp = await CorrespondenceService.deleteBuyerMessage(rfxRoundId, threadId, messageId, listResourceId);
        }
        if (delResp.success !== true) {
            isResponseValid(delResp, setError);
        }
    }

    const handleSubmit = (type, formikProps, values) => {
        autoSaveSemaphore.current = false;
        if (formikProps) {
            values = formikProps.values;
        }
        setWorking(true);
        setError('');

        if ("send" === type || "publish" === type) {

            Yup.object().shape({
                contents: VS.string(),
                supplierRecipientIds: contractor ? VS.any() : VS.arrayRequired()
            }).validate(values, {abortEarly: false}).then(x => {
                if ("send" === type) {
                    updateMessage(type, x);
                } else if ("publish" === type) {
                    setPublishMessageInfo(createConfirmObject(values, () => updateMessage(type, x), cancelPublish));
                }
            }).catch(x => {
                const ob = {};
                x.inner.reverse().forEach(x => ob[x.path] = x.message);
                formikProps.setErrors(ob);
                setWorking(false);
            })
        } else {
            updateMessage(type, values);
        }

    }


    const onSubmit = (values) => {
        handleSubmit('autoSave', null, values);
    };

    const autoSaveDebounced = _.debounce(() => {
        if (autoSaveSemaphore.current) {
            formSubmit[formId]();
        }
    }, 8000);

    const autoSave = () => {
        autoSaveSemaphore.current = true;
        autoSaveDebounced();
    }


    return (
        <div className={clsx(classes.root)}>

            <div className={clsx(classes.contentWrapper)}>
                <div className={classes.content}>
                    <GridBlock justify='flex-end'>
                        <BoldTypography variant='caption'>{`${Tr('last_save_date')}: `}</BoldTypography>
                        <Typography variant='caption' component='p' className={classes.sendDate}>
                            {message.modifiedDate ? moment(message.modifiedDate).format(DateFormats.DATETIME_WITH_SECONDS_FORMAT) : ''}
                        </Typography>
                    </GridBlock>
                    <Form
                        initialValues={{
                            contents: message.contents,
                            supplierRecipientIds: message.recipients.map(x => x.id),
                            attachments: message.attachments
                        }}
                        initialTouched={{
                            contents: true,
                            supplierRecipientIds: true,
                            attachments: true
                        }}
                        onSubmit={onSubmit}
                        validationSchema={VS.any()}
                        id={formId}
                    >
                        {(formikProps) => (
                            <NewMessageForm formikProps={formikProps} message={message} rfxRoundId={rfxRoundId} autoSave={autoSave} updateMessage={handleSubmit}
                                            deleteMessage={deleteMessage} error={error} working={working} contractor={contractor}
                                            currentActive={currentActive}/>
                        )}
                    </Form>
                </div>
            </div>
            {(!contractor && !!publishMessageInfo) && <PublishMessageConfirmationPopup publishMessageInfo={publishMessageInfo}/>}
        </div>
    )
}

NewMessage.propTypes = {
    classes: PropTypes.any,
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(NewMessage);